/* DEPTH OF FIELD — a lens's blur, for the deliverable.

   What it is FOR on a 3D billboard: the eye of someone in the street focuses
   on the physical screen, so nothing here is optics the audience would
   otherwise see. It is a picture-making choice, the same one a photographer
   makes — the back of the box going soft while the hero stays sharp is one
   more cue that the box is deep. So it is controlled the way a picture is
   judged, by one Blur slider and a subject to keep sharp, not by f-stops:
   at billboard viewing distances a real aperture gives almost no blur, and
   the numbers would have to be exaggerated to mean anything anyway.

   How much a point blurs follows a lens all the same: in proportion to how far
   it is from the focus, RELATIVE to how far it is from the viewer
   (|d − focus| / d, the thin-lens circle of confusion), so the same Blur reads
   the same on a 1 m sign and a 20 m wall, and near things blur faster than
   far ones, as they do through any real lens. The biggest blur is a share of
   the picture's height, so the preview and a supersampled export match.

   The blur is Dennis Gustafsson's single-pass "scatter as gather" bokeh: each
   pixel gathers samples on a spiral out to the largest blur there is, and
   takes one only if THAT sample's own blur reaches this far. So a blurred
   foreground spreads over a sharp background, but a sharp foreground is never
   smeared by the background behind it, which is the edge a naive blur gets
   wrong. Colours are blurred in linear light, so a bright highlight spreads
   into a bright disc rather than a grey smudge.

   Depth comes from the pass ambient occlusion already draws (scene/ao.js), so
   the two can never disagree about where things are. Applied last, over the
   finished frame — blend-mode layers included — and only through the screen's
   own cameras: Orbit is a place to work, and a blurred workspace helps nobody.
   At Blur 0 none of it runs. */
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { drawDepth } from './ao.js';
import { orbitCam, renderer } from './context.js';
import { S, clamp } from '../state/store.js';

/* The largest blur, as a share of the picture's height, at Blur 1. */
const MAX_BLUR = .02;
/* How fast blur grows with distance from the focus: at this relative
   distance, (d − focus) / d = 1 / K, a point is fully blurred. 4 puts the back
   of a typical inset (a quarter of the viewing distance behind the screen)
   near full blur while an object a little in front stays readable. */
const K = 4;
const TAPS = 96;

let T = null;

function build(){
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      tColor: { value: null }, tDepth: { value: null },
      projInv: { value: new THREE.Matrix4() },
      res: { value: new THREE.Vector2() },
      maxPx: { value: 0 }, focus: { value: 1 }, k: { value: K }
    },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `
      #define TAPS ${TAPS}
      uniform sampler2D tColor; uniform highp sampler2D tDepth;
      uniform mat4 projInv; uniform vec2 res;
      uniform float maxPx; uniform float focus; uniform float k;
      varying vec2 vUv;

      float viewDepth(vec2 uv){
        float d = texture2D(tDepth, uv).x;
        if(d >= 1.0) return 1e6;                       // nothing drawn: as far as it gets
        vec4 v = projInv * vec4(vec3(uv, d) * 2.0 - 1.0, 1.0);
        return max(1e-4, -v.z / v.w);
      }
      float blurPx(float z){ return maxPx * clamp(abs(z - focus) / z * k, 0.0, 1.0); }
      vec4 toLinear(vec4 c){ return vec4(pow(c.rgb, vec3(2.2)), c.a); }

      void main(){
        float cz = viewDepth(vUv);
        float cs = blurPx(cz);
        vec4 acc = toLinear(texture2D(tColor, vUv));
        float tot = 1.0;
        /* A different spiral rotation per pixel, so the taps' pattern breaks
           into fine grain instead of repeating as visible rings. */
        float rot = 6.2831853 * fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
        for(int i = 0; i < TAPS; i++){
          float r = maxPx * sqrt((float(i) + 0.5) / float(TAPS));
          float a = float(i) * 2.39996323 + rot;           // the golden angle
          vec2 uv = vUv + vec2(cos(a), sin(a)) * r / res;
          float sz = viewDepth(uv);
          float ss = blurPx(sz);
          /* Something BEHIND this pixel cannot blur over it by more than this
             pixel is blurred itself — that is what keeps a sharp subject's edge
             from being eaten by the soft background behind it. */
          if(sz > cz) ss = min(ss, cs * 2.0);
          float m = smoothstep(r - 1.0, r + 1.0, ss);
          acc += mix(acc / tot, toLinear(texture2D(tColor, uv)), m);
          tot += 1.0;
        }
        vec4 c = acc / tot;
        gl_FragColor = vec4(pow(max(c.rgb, 0.0), vec3(1.0 / 2.2)), c.a);
      }`,
    blending: THREE.NoBlending, depthTest: false, depthWrite: false
  });
  T = { mat, quad: new FullScreenQuad(mat), colour: null, w: 0, h: 0 };
}

const _box = new THREE.Box3(), _p = new THREE.Vector3(), _size = new THREE.Vector2(), _origin = new THREE.Vector2();

/* Where the focus is, as a distance along the camera's axis. On an object, it
   is that object's centre — looked up every frame, so the focus follows it
   through an animation. Otherwise a depth in front of (+) or behind (−) the
   screen, the screen surface itself by default. An object that has been
   deleted falls back to the screen surface. */
function focusDepth(cam){
  const f = S.dofFocus || {};
  const o = f.mode === 'object' ? S.objects.find(x => x.id === f.id && x.node && x.visible !== false) : null;
  if(o){
    o.node.updateMatrixWorld(true);
    _box.setFromObject(o.node);
    if(_box.isEmpty()) _p.copy(o.node.getWorldPosition(_p)); else _box.getCenter(_p);
  }else _p.set(0, 0, f.mode === 'custom' ? (+f.z || 0) : 0);
  _p.applyMatrix4(cam.matrixWorldInverse);
  return Math.max(1e-3, -_p.z);
}

/**
 * Blur the frame the renderer just finished in the canvas. A no-op at Blur 0
 * and through the Orbit camera, so callers do not need to ask first.
 */
export function applyDOF(cam){
  const strength = clamp(+S.dofStrength || 0, 0, 1);
  if(strength <= 0 || cam === orbitCam) return;
  if(!T) build();

  renderer.getDrawingBufferSize(_size);
  const w = Math.max(1, _size.x | 0), h = Math.max(1, _size.y | 0);
  if(!T.colour || T.w !== w || T.h !== h){
    T.colour?.dispose();
    T.colour = new THREE.FramebufferTexture(w, h);
    T.w = w; T.h = h;
  }

  const target = renderer.getRenderTarget();
  const depth = drawDepth(cam);
  /* The finished frame, copied off the canvas: the blur reads it while
     writing over the same canvas, which it cannot do from the canvas itself. */
  renderer.setRenderTarget(target);
  renderer.copyFramebufferToTexture(_origin, T.colour);

  const u = T.mat.uniforms;
  u.tColor.value = T.colour;
  u.tDepth.value = depth;
  u.projInv.value.copy(cam.projectionMatrixInverse);
  u.res.value.set(w, h);
  u.maxPx.value = strength * MAX_BLUR * h;
  u.focus.value = focusDepth(cam);

  const auto = renderer.autoClear;
  renderer.autoClear = false;
  T.quad.render(renderer);
  renderer.autoClear = auto;
}
